/**
 * Pure derivations from save state for the stats screen.
 *
 * All functions are deterministic given a SaveState + reference date —
 * no DOM, no side effects — so the stats screen can render them
 * deterministically and they can be unit-tested in isolation.
 *
 * Date handling uses the same UTC convention as todaysSeedString() so the
 * stats line up with daily entries.
 */

import { computeStreak, todaysSeedString, type SaveState } from '../persistence/saveState';

export interface OverallStats {
  /** Number of unique days the player has any record for. */
  daysPlayed: number;
  /** Days where the FIRST attempt was a completed win. */
  daysWon: number;
  /** Win percentage by first attempt (0..100). */
  winPercent: number;
  /** Current consecutive streak (see computeStreak). */
  currentStreak: number;
  /** Longest streak ever achieved. */
  bestStreak: number;
  /** Total attempts across all days (replays included). */
  totalAttempts: number;
  /** Best first-attempt score across all days. */
  bestFirstScore: number;
  /** Best score ever (any attempt, any day). */
  bestEverScore: number;
}

/** Compute overall daily-mode stats from the save state. */
export function overallStats(state: SaveState, today: Date = new Date()): OverallStats {
  const entries = Object.values(state.daily);
  const daysPlayed = entries.length;
  const daysWon = entries.filter((e) => e.firstAttemptComplete).length;
  const totalAttempts = entries.reduce((sum, e) => sum + e.attempts, 0);
  const bestFirstScore = entries.reduce((m, e) => Math.max(m, e.firstAttemptScore), 0);
  const bestEverScore = entries.reduce((m, e) => Math.max(m, e.bestScore), 0);
  const winPercent = daysPlayed === 0 ? 0 : Math.round((daysWon / daysPlayed) * 100);

  return {
    daysPlayed,
    daysWon,
    winPercent,
    currentStreak: computeStreak(state, today),
    bestStreak: longestStreak(state),
    totalAttempts,
    bestFirstScore,
    bestEverScore,
  };
}

/**
 * Longest consecutive run of first-attempt-completed days ever achieved.
 * Walks all daily keys in chronological order and tracks the longest run
 * where each day is the previous day + 1 and the first attempt was a win.
 */
export function longestStreak(state: SaveState): number {
  const completedDays = Object.values(state.daily)
    .filter((e) => e.firstAttemptComplete)
    .map((e) => e.date)
    .sort();

  if (completedDays.length === 0) return 0;

  let best = 1;
  let current = 1;
  for (let i = 1; i < completedDays.length; i++) {
    const prev = completedDays[i - 1]!;
    const curr = completedDays[i]!;
    if (isNextDay(prev, curr)) {
      current++;
      if (current > best) best = current;
    } else {
      current = 1;
    }
  }
  return best;
}

/** True if `b` is the calendar day immediately after `a` (UTC). */
function isNextDay(a: string, b: string): boolean {
  const da = new Date(a + 'T00:00:00Z');
  const db = new Date(b + 'T00:00:00Z');
  const expected = new Date(da);
  expected.setUTCDate(da.getUTCDate() + 1);
  return todaysSeedString(db) === todaysSeedString(expected);
}

// --- Calendar -------------------------------------------------------------

export type DayStatus =
  | 'won'        // first attempt completed
  | 'attempted'  // played but first attempt failed
  | 'skipped'    // no entry for that day
  | 'today-unplayed';

export interface CalendarDay {
  dateKey: string;
  status: DayStatus;
  bestScore: number;
}

/**
 * Build a calendar of the last N days ending on `today` (inclusive). The
 * default of 28 days = a clean four-week mini-grid for the stats screen.
 */
export function recentCalendar(
  state: SaveState,
  days: number = 28,
  today: Date = new Date()
): CalendarDay[] {
  const out: CalendarDay[] = [];
  const todayKey = todaysSeedString(today);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = todaysSeedString(d);
    const entry = state.daily[key];
    let status: DayStatus;
    if (!entry) {
      status = key === todayKey ? 'today-unplayed' : 'skipped';
    } else if (entry.firstAttemptComplete) {
      status = 'won';
    } else {
      status = 'attempted';
    }
    out.push({ dateKey: key, status, bestScore: entry?.bestScore ?? 0 });
  }
  return out;
}

// --- Score histogram ------------------------------------------------------

export interface HistogramBucket {
  /** Inclusive lower bound. */
  from: number;
  /** Exclusive upper bound. (Last bucket is +Infinity.) */
  to: number;
  /** Number of days falling into this bucket. */
  count: number;
}

/**
 * Distribution of FIRST-attempt daily scores into fixed buckets. Uses first-
 * attempt scores (not best-ever) so the histogram represents the player's
 * cold-start ability rather than their optimized replay performance.
 */
export function scoreHistogram(state: SaveState): HistogramBucket[] {
  const buckets: HistogramBucket[] = [
    { from: 0,    to: 500,  count: 0 },
    { from: 500,  to: 1000, count: 0 },
    { from: 1000, to: 1500, count: 0 },
    { from: 1500, to: 2000, count: 0 },
    { from: 2000, to: 2500, count: 0 },
    { from: 2500, to: 3000, count: 0 },
    { from: 3000, to: 3500, count: 0 },
    { from: 3500, to: Infinity, count: 0 },
  ];
  for (const entry of Object.values(state.daily)) {
    const s = entry.firstAttemptScore;
    for (const b of buckets) {
      if (s >= b.from && s < b.to) {
        b.count++;
        break;
      }
    }
  }
  return buckets;
}
